import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom'; // <-- NEW IMPORT
import axios from 'axios';
import { FileCode2, Download, ShieldCheck, AlertCircle, ArrowLeft } from 'lucide-react'; // <-- ADDED ArrowLeft

export default function AiBomRegistry() {
  const { user, token } = useAuth();
  const navigate = useNavigate(); // <-- INITIALIZE NAVIGATION
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !user.org_id) return;

    const fetchInventory = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await axios.get(`${baseURL}/api/inventory`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Only show sanctioned/approved tools for official AI-BOMs
        const sanctionedTools = res.data.filter(t => t.sanctioned);
        setTools(sanctionedTools);
      } catch (error) {
        console.error("Failed to load inventory for AI-BOM", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [user, token]);

  const handleDownload = async (toolId, toolName, version) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.get(`${baseURL}/api/aibom/generate/${toolId}?version=${version}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob' 
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AIBOM_${toolName.replace(/\s+/g, '_')}_SPDX_${version.replace('.', '_')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download AI-BOM", error);
      alert("Failed to generate AI-BOM. Please check your permissions.");
    }
  };

  if (loading) return <div className="p-8 text-slate-500 text-center font-medium">Loading Compliance Registry...</div>;

  return (
    <div className="max-w-7xl mx-auto p-8 pb-24">
      
      

      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-3">
            <FileCode2 className="w-8 h-8 text-blue-600" /> AI-BOM Registry
          </h1>
          <p className="text-slate-500 mt-2 font-medium">Generate EU AI Act compliant SPDX manifests (ISO/IEC 5962) for your sanctioned systems.</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm">
          <ShieldCheck className="w-5 h-5" /> 
          SPDX 2.3 & 3.0 Ready
        </div>
      </div>

      {tools.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 shadow-sm text-center">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-800 mb-2">No Sanctioned Tools Found</h3>
          <p className="text-slate-500 text-sm">You must map and approve AI tools in your AI Inventory before generating compliance manifests.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="p-5 font-bold">System / Application</th>
                <th className="p-5 font-bold">Vendor (Supplier)</th>
                <th className="p-5 font-bold">Risk Classification</th>
                <th className="p-5 font-bold text-right">Compliance Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {tools.map((tool) => (
                <tr key={tool.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-5 font-bold text-slate-900">{tool.tool_name}</td>
                  <td className="p-5 text-slate-600">{tool.vendor}</td>
                  <td className="p-5">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      tool.eu_ai_act_risk === 'High' ? 'bg-amber-100 text-amber-800' :
                      tool.eu_ai_act_risk === 'Unacceptable' ? 'bg-red-100 text-red-800' :
                      'bg-emerald-100 text-emerald-800'
                    }`}>
                      {tool.eu_ai_act_risk || 'Unassessed'}
                    </span>
                  </td>
                  <td className="p-5 text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => handleDownload(tool.id, tool.tool_name, '2.3')}
                        className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 font-bold py-1.5 px-3 rounded-lg shadow-sm transition-colors text-xs"
                        title="Best for broad compatibility with current systems"
                      >
                        <Download className="w-3.5 h-3.5" />
                        v2.3 (Legacy)
                      </button>
                      <button 
                        onClick={() => handleDownload(tool.id, tool.tool_name, '3.0')}
                        className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-blue-600 text-white font-bold py-1.5 px-3 rounded-lg shadow-sm transition-colors text-xs"
                        title="Best for modern AI, Dataset, and Model specific profiles"
                      >
                        <Download className="w-3.5 h-3.5" />
                        v3.0 (AI Profile)
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}